import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef, OnDestroy,
    OnInit,
    signal,
    Signal,
    viewChild
} from '@angular/core';
import {MatButton} from "@angular/material/button";
import {BulkResponse, DomainService, NameServerGroup} from "../../services/domain.service";
import {BusyService} from "../../services/busy.service";
import {finalize, mergeMap} from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import {AlertService} from "../../services/alert.service";
import {Observable, Subscription, timer} from "rxjs";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {UtilService} from "../../services/util.service";
import {FormsModule} from "@angular/forms";
import {MatInput} from "@angular/material/input";
import {AuthService} from "../../services/auth.service";
import {MatPaginator} from "@angular/material/paginator";
import {Router} from "@angular/router";
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from "@angular/material/table";
import {TaskGroup, TaskService} from "../../services/task.service";
import {Constants} from "../../util/constants";
import {DecimalPipe} from "@angular/common";
import {NotificationService} from "../../services/notification.service";
import {MatTooltip} from "@angular/material/tooltip";
import * as XLSX from 'xlsx';

type BulkOperation = (domains:string[]) => Observable<BulkResponse>;

@Component({
    imports: [
        MatButton,
        MatFormField,
        MatOption,
        MatSelect,
        FormsModule,
        MatLabel,
        MatInput,
        MatPaginator,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatHeaderCell,
        MatTable,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatHeaderCellDef,
        DecimalPipe,
        MatTooltip
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './bulk.component.html',
    styleUrl: './bulk.component.scss'
})
export class BulkComponent implements OnInit, OnDestroy {

    setFlagUpload = viewChild<ElementRef>('setFlagUpload');
    clearFlagUpload = viewChild<ElementRef>('clearFlagUpload');
    lockUpload = viewChild<ElementRef>('lockUpload');
    unlockUpload = viewChild<ElementRef>('unlockUpload');
    dnsUpload = viewChild<ElementRef>('dnsUpload');
    deleteUpload = viewChild<ElementRef>('deleteUpload');

    nsGroups = signal<NameServerGroup[]>([]);
    selectedGroup = signal('');
    // 2FA code to authorize the enabling of the Delete button
    allowDeleteCode = signal('');
    // We have a 2FA code that looks good enough to attempt validation
    canAllowDelete = computed(() => /^\d{6}$/.test(this.allowDeleteCode()));
    // Validation of 2FA code has succeeded, so bulk delete scheduling can proceed
    canDelete = signal(false);

    paginator = viewChild<MatPaginator>('paginator');
    dataSource = signal(new MatTableDataSource<TaskGroup>([]));

    taskGroups = computed(() =>
        this.dataSource()? this.dataSource().data : []
    );

    flags = signal<String[]>([]);
    selectedFlag = signal('');

    public readonly DEFAULT_PAGE_SIZE = 10;

    readonly columnsToDisplay = ['id', 'type', 'other', 'origin', 'user', 'created', 'processed', 'time_left',
                                 'task_count', 'completed_task_count', 'failed_task_count'];

    private notifSub?: Subscription;
    private timerSub?: Subscription;

    constructor(private domainService: DomainService,
                private alertService: AlertService,
                private authService: AuthService,
                private utilService: UtilService,
                private taskService: TaskService,
                private router: Router,
                private notificationService: NotificationService,
                private busyService: BusyService) {

    }

    ngOnInit() {
        // If the server sends a notification that a task group has been created, update the task groups
        this.notifSub = this.notificationService.message$.subscribe((message) => {
            if (message == "bulk")
                this.updateTaskGroups();
        });
        this.busyService.showBusy();
        this.domainService.getAllFlags().pipe(
            mergeMap(flags => {
                this.flags.set(flags);
                return this.utilService.getGroups()
            }),
            finalize(() => { this.busyService.showNotBusy() })
        ).subscribe( {
            next: (groups) => {
                this.nsGroups.set(groups);
                this.updateTaskGroups();
            },
            error: (err: HttpErrorResponse) => {
                console.error(err);
                this.alertService.error("Error retrieving name server groups: " + err.message);
            }
        });
    }

    ngOnDestroy() {
        this.notifSub?.unsubscribe();
        this.timerSub?.unsubscribe();
    }

    private updateTaskGroups() {
        this.busyService.showBusy();
        this.taskService.getAllGroups().pipe(
            finalize(() => {this.busyService.showNotBusy()})
        ).subscribe( {
            next: (groups) => {
                let source = new MatTableDataSource<TaskGroup>(groups);
                if (this.paginator())
                    this.paginator()!.pageIndex = 0;
                source.paginator = this.paginator() || null;
                this.dataSource.set(source);
                // If any groups are processing, set up a timer to update the task groups periodically
                if (groups.some(g => g.processing))
                    this.timerSub = timer(5000).subscribe(() => {this.updateTaskGroups()});
            },
            error: (err: HttpErrorResponse) => {
                console.error(err);
                this.alertService.error("Error retrieving task groups: " + err.message);
            }
        })
    }

    setFlagLoaded(ev: Event): void {
        this.changeFlagLoaded(ev, true);
    }

    clearFlagLoaded(ev: Event): void {
        this.changeFlagLoaded(ev, false);
    }

    private changeFlagLoaded(ev: Event, flag: boolean): void {
        const elem = ev.target as HTMLInputElement;
        if (elem.files && elem.files[0]) {
            const file = elem.files[0];
            console.log("file:", file.name);

            if (this.isExcelFile(file)) {
                this.processExcelFile(file, (domains) => {
                    this.changeFlag(domains, this.selectedFlag(), flag);
                    this.uploadReset(flag? this.setFlagUpload()! : this.clearFlagUpload()!);
                });
            } else {
                let reader = new FileReader();
                reader.addEventListener("load", () => {
                    const domains = this.preprocessList(reader.result as string);
                    this.changeFlag(domains, this.selectedFlag(), flag);
                    this.uploadReset(flag? this.setFlagUpload()! : this.clearFlagUpload()!);
                }, false);
                reader.readAsText(file);
            }
        }
    }

    private isExcelFile(file: File): boolean {
        return file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    }

    private processExcelFile(file: File, callback: (domains: string[]) => void): void {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            // Assume the first sheet contains the domain list
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convert the sheet data to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

            // Extract domains from the first column of each row
            let domains: string[] = [];
            jsonData.forEach(row => {
                if (row.length > 0 && row[0]) {
                    const domain = String(row[0]).toLowerCase().trim();
                    if (domain.length > 0) {
                        domains.push(domain);
                    }
                }
            });

            // Remove duplicates and sort
            domains = [...new Set(domains)].sort();

            callback(domains);
        };
        reader.readAsArrayBuffer(file);
    }

    private uploadReset(fileInput: ElementRef): void {
        // Must also allow the file input element to forget its prior file, in case user wants to upload the same
        // file again. Otherwise, the change event doesn't get triggered because nothing has changed. This seems to be
        // the cleanest way to achieve it.
        fileInput.nativeElement.type = "";
        fileInput.nativeElement.type = "file";
    }

    private preprocessList(body: string): string[] {
        // Get one domain from each line, as lowercase, removing leading or trailing spaces, and skipping empty lines
        let domains = body.split('\n')
            .map((line) => line.toLocaleLowerCase().trim())
            .filter(line => line.length > 0);
        // Now remove duplicates and sort it
        return [...new Set(domains)].sort();
    }

    private changeFlag(domains: string[], flagName: string, flagValue: boolean): void {
        const op = flagValue? "set" : "clear";
        this.alertService.confirm(`Are you sure you want to ${op} the ${flagName} flag for ${domains.length} domains?`).subscribe({
            next: (confirmed) => {
                if (confirmed)
                    this.reallyChangeFlag(domains, flagName, flagValue)
            }
        });
    }

    private reallyChangeFlag(domains: string[], flagName: string, flag: boolean): void {
        console.log(`Uploading ${domains.length} domains`);
        this.busyService.showBusy();
        const obs: Observable<BulkResponse> = flag? this.domainService.bulkSetFlag(flagName, domains)
                                                  : this.domainService.bulkClearFlag(flagName, domains);
        obs.pipe(
            finalize(() => this.busyService.showNotBusy())
        ).subscribe({
            next: (br: BulkResponse) => {
                this.alertService.info(this.buildMessageFromBulkResponse(domains, br, false));
            },
            error: (err: HttpErrorResponse) => {
                this.alertService.error(`Error changing ${flagName} flag for domains: ` + err.error?.message || err.error || err.message);
            }
        })
    }

    lockLoaded(ev: Event): void {
        this.handleQueuedOpLoaded(ev.target as HTMLInputElement, this.lockUpload, 'lock',
                                  (d) => this.domainService.bulkLockDomains(d));
    }

    unlockLoaded(ev: Event): void {
        this.handleQueuedOpLoaded(ev.target as HTMLInputElement, this.unlockUpload, 'unlock',
                                  (d) => this.domainService.bulkUnlockDomains(d));
    }

    dnsLoaded(ev: Event): void {
        this.handleQueuedOpLoaded(ev.target as HTMLInputElement, this.dnsUpload, 'ns_change',
            (d) => this.domainService.bulkChangeDomainsNsGroup(d, this.selectedGroup()));
    }

    deleteClicked(): void {
        const question = "Are you sure you want to delete a list of domains?<br/>This action cannot be undone!";
        this.alertService.confirm(question).subscribe({
            next: (confirmed) => {
                if (confirmed)
                    this.deleteUpload()!.nativeElement.click();
            }
        });
    }

    deleteLoaded(ev: Event): void {
        this.handleQueuedOpLoaded(ev.target as HTMLInputElement, this.deleteUpload, 'delete',
                                  (d) => this.domainService.bulkDeleteDomains(d));
    }

    private handleQueuedOpLoaded(inputElem: HTMLInputElement,
                                 childSignal: Signal<ElementRef | undefined>,
                                 opName: string, op: BulkOperation) {
        if (inputElem.files && inputElem.files[0]) {
            const file = inputElem.files[0];
            console.log("file:", file.name);

            if (this.isExcelFile(file)) {
                this.processExcelFile(file, (domains) => {
                    this.queueBulkOperation(domains, opName, op);
                    this.uploadReset(childSignal()!);
                });
            } else {
                let reader = new FileReader();
                reader.addEventListener("load", () => {
                    const domains = this.preprocessList(reader.result as string);
                    this.queueBulkOperation(domains, opName, op);
                    this.uploadReset(childSignal()!);
                }, false);
                reader.readAsText(file);
            }
        }
    }

    private buildMessageFromBulkResponse(domains: string[], br: BulkResponse, queued: boolean): string {
        const op = queued? "Queued" : "Updated";
        if (br.remainingDomains?.length == domains.length)
            return `${op} domains: ${domains.length}`;

        let line = `${op} domains: ${br.remainingDomains?.length || 0}`;
        let msg = `${line}<br/>`;
        let details = `${line}`;

        if (br.notInRegistry && br.notInRegistry.length > 0) {
            line = `Not in registry: ${br.notInRegistry.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.notInRegistry.map(domain => `  ${domain}`).join('\n');
        }
        if (br.alreadyDeleted && br.alreadyDeleted.length > 0) {
            line = `Already deleted: ${br.alreadyDeleted.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.alreadyDeleted.map(domain => `  ${domain}`).join('\n');
        }
        if (br.alreadyDone && br.alreadyDone.length > 0) {
            line = `Already in requested state: ${br.alreadyDone.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.alreadyDone.map(domain => `  ${domain}`).join('\n');
        }
        if (br.inPrimaryDns && br.inPrimaryDns.length > 0) {
            line = `In primary DNS: ${br.inPrimaryDns.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.inPrimaryDns.map(domain => `  ${domain}`).join('\n');
        }
        if (br.outOfGracePeriod && br.outOfGracePeriod.length > 0) {
            line = `Out of delete grace period: ${br.outOfGracePeriod.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.outOfGracePeriod.map(domain => `  ${domain}`).join('\n');
        }
        if (br.inServerHold && br.inServerHold.length > 0) {
            line = `In serverHold status: ${br.inServerHold.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.inServerHold.map(domain => `  ${domain}`).join('\n');
        }
        if (br.inServerDeleteProhibited && br.inServerDeleteProhibited.length > 0) {
            line = `In serverDeleteProhibited status: ${br.inServerDeleteProhibited.length}`;
            msg += `<p>${line}</p>`;
            details += `\n\n${line}\n` + br.inServerDeleteProhibited.map(domain => `  ${domain}`).join('\n');
        }
        if (br.flagExcluded) {
            const flags = Object.keys(br.flagExcluded);
            flags.forEach(flag => {
                if (br.flagExcluded![flag].length > 0) {
                    line = `Excluded due to '${flag}' flag: ${br.flagExcluded![flag].length}`;
                    msg += `<p>${line}</p>`;
                    details += `\n\n${line}\n` + br.flagExcluded![flag].map(domain => `  ${domain}`).join('\n');
                }
            });
        }
        const blob = new Blob([details], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        msg += `<p>Download <a href="${url}" download="excluded.txt">full list</a> of excluded files</p>`;
        return msg;
    }

    private queueBulkOperation(domains: string[], opName: string, op: BulkOperation) {
        this.alertService.confirm(`Are you sure you want to perform the ${opName} operation on ${domains.length} domains?`)
            .subscribe({
                next: (confirmed) => {
                    if (confirmed)
                        this.reallyQueueBulkOperation(domains, opName, op);
                }
            });
    }

    private reallyQueueBulkOperation(domains: string[], opName: string, op: BulkOperation) {
        console.log(`Uploading ${domains.length} domains for ${opName} operation`);
        this.busyService.showBusy();
        op(domains).pipe(
            finalize(() => this.busyService.showNotBusy())
        ).subscribe({
            next: (br: BulkResponse) => {
                // Report to the user the result of the call, including info about domains that won't be processed.
                // After the dialog is closed, ensure that the task groups table is updated if there was anything to
                // be processed.
                this.alertService.infoObs(this.buildMessageFromBulkResponse(domains, br, true)).subscribe(() => {
                    if (br.remainingDomains && br.remainingDomains.length > 0)
                        this.updateTaskGroups();
                });
            },
            error: (err: HttpErrorResponse) => {
                this.alertService.error(`Error queuing ${opName} operation: ` + err.error?.message || err.error || err.message);
            }
        })
    }

    /** Validates the entered 2FA code and, if valid, enables the Delete button
     */
    authorizeDelete() {
        this.busyService.showBusy()
        this.authService.validateMfaCode(this.allowDeleteCode()).pipe(
            finalize(() => {
                this.busyService.showNotBusy()
            })
        ).subscribe( {
            next: (valid) => {
                if (!valid)
                    this.alertService.error("The supplied 2FA code is not valid");
                else {
                    this.allowDeleteCode.set('')
                    this.canDelete.set(true);
                }
            },
            error: (err: HttpErrorResponse) => {
                this.alertService.error("Error validating 2FA code: " + err.error?.message || err.message);
            }
        });
    }

    /** Estimates time left to complete processing all the tasks in this group by extrapolating from the time it
     * has taken for the tasks processed so far and returns it as a formatted string
     *
     * @param group the task group for which to estimate the time left
     * @return time left to process, formatted as a string h:mm:ss
     */
    estimateTimeLeft(group: TaskGroup): string {
        if (!group.processing)
            return "&check;"
        const processedTaskCount = group.completed_task_count + group.failed_task_count;
        if (processedTaskCount == 0)
            return "&hellip;"
        const secSoFar = (Date.now() - group.created)/1000;
        const timePerTask = secSoFar/processedTaskCount;
        const secLeft = Math.round(group.task_count * timePerTask - secSoFar);
        if (secLeft < 0)
            return "&hellip;"
        const hours = Math.floor(secLeft / 3600);
        const mins = Math.floor((secLeft - hours*3600) / 60);
        const secs = secLeft % 60;
        const zMins = mins.toString().padStart(2, '0')
        const zSecs = secs.toString().padStart(2, '0')
        return `${hours}:${zMins}:${zSecs}`;
    }

    formatTimestamp(timestamp: number): string {
        return timestamp? Constants.US_TIMESTAMP_FORMAT.format(new Date(timestamp)) : "";
    }

    goToGroup(groupId: number) {
        this.router.navigateByUrl(`/bulk/task_details/${groupId}`).then();
    }
}
